// Time-filter utilities — shared between <TimeFilter> and all chart widgets.

export type TimePreset =
  | 'today' | 'yesterday' | 'this_week' | '2_weeks'
  | 'this_month' | 'this_q' | 'this_year' | 'custom'

export type Granularity = 'day' | 'week' | 'month'

export interface TimeRange {
  preset: TimePreset
  from: string        // YYYY-MM-DD
  to: string          // YYYY-MM-DD
  granularity: Granularity
}

// Default granularity for each preset
const DEFAULT_GRAN: Record<TimePreset, Granularity> = {
  today:      'day',
  yesterday:  'day',
  this_week:  'day',
  '2_weeks':  'day',
  this_month: 'day',
  this_q:     'week',
  this_year:  'month',
  custom:     'day',
}

const fmt = (d: Date) => d.toISOString().slice(0, 10)

export function getRange(
  preset: TimePreset,
  customFrom?: string,
  customTo?: string,
  granularity?: Granularity,
): TimeRange {
  const now   = new Date()
  const today = fmt(now)
  let from    = today
  let to      = today

  switch (preset) {
    case 'today':
      break

    case 'yesterday': {
      const d = new Date(now)
      d.setDate(d.getDate() - 1)
      from = fmt(d)
      to   = fmt(d)
      break
    }

    case 'this_week': {
      const d   = new Date(now)
      const dow = d.getDay()
      d.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1))
      from = fmt(d)
      break
    }

    case '2_weeks': {
      const d = new Date(now)
      d.setDate(d.getDate() - 13)
      from = fmt(d)
      break
    }

    case 'this_month':
      from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
      break

    case 'this_q': {
      const qStart = Math.floor(now.getMonth() / 3) * 3
      from = `${now.getFullYear()}-${String(qStart + 1).padStart(2, '0')}-01`
      break
    }

    case 'this_year':
      from = `${now.getFullYear()}-01-01`
      break

    case 'custom':
      from = customFrom ?? today
      to   = customTo   ?? today
      break

    default:
      from = `${now.getFullYear()}-01-01`
  }

  return {
    preset,
    from,
    to,
    granularity: granularity ?? DEFAULT_GRAN[preset],
  }
}

export const PRESET_LABELS: Record<TimePreset, string> = {
  today:      'Today',
  yesterday:  'Yest',
  this_week:  'Week',
  '2_weeks':  '2 wks',
  this_month: 'Month',
  this_q:     'Qtr',
  this_year:  'Year',
  custom:     'Custom',
}

// Custom is no longer a button — dates are always editable
export const ALL_PRESETS: TimePreset[] = [
  'today', 'yesterday', 'this_week', '2_weeks',
  'this_month', 'this_q', 'this_year',
]

export const GRAN_LABELS: Record<Granularity, string> = {
  day:   'Day',
  week:  'Week',
  month: 'Month',
}
export const ALL_GRANS: Granularity[] = ['day', 'week', 'month']
